import React, { useEffect, useMemo } from 'react';
import throttle from 'lodash/throttle';
import { connect } from 'redux-zero/react';
import { useAdInFeed } from '@ads/hooks/useAdInFeed';
import { useAdsOnly } from '@ads/hooks/useAdsOnly';
import { putAdsToPositionInFeed } from '@ads/utils/putAdsToPositionInFeed';
import ErrorBoundary from '@embed/components/common/ErrorBoundary';
import { GlobalCustomCSS } from '@embed/components/common/GlobalCustomCSS';
import QuantcastTag from '@embed/components/common/QuantcastTag';
import Grid from '@embed/components/Grid';
import { AppContextType } from '@embed/components/helpers';
import { WidgetWrapper } from '@embed/components/Player3/components/WidgetWrapper';
import { useEmbedAppContext, } from '@embed/hooks/useEmbedAppContext';
import { useEmbedFeed } from '@embed/hooks/useEmbedFeed';
const mapStateToProps = ({ appContext, largeCarousel, params, player, placeholders, }) => {
    return {
        appContext: appContext,
        largeCarousel: (largeCarousel || params.size === 'large') &&
            params.mode !== 'pinned',
        params: params,
        player,
        placeholders,
    };
};
const PureEmbedFeed = (props) => {
    const { appContext, store, params, placeholders, player, actions, largeCarousel, } = props;
    useEffect(() => {
        actions.setContext({ appContext });
    }, [appContext]);
    useEffect(() => {
        actions.trackEmbedInstance({ appContext });
    }, [appContext.embedInstanceId]);
    const { publisherClient, channel, embedFeedConfig } = appContext;
    const [{ loading, videos: feedVideos, hasMore, playlists }, feedActions] = useEmbedFeed({
        config: embedFeedConfig,
        guestId: appContext.guestId,
        params,
        performance: appContext.performance,
    });
    const isAdsOnly = useAdsOnly({ appContext });
    const { adVideos, positions, loading: adVideosLoading, } = useAdInFeed({
        appContext,
        params,
    });
    const videos = useMemo(() => {
        if (isAdsOnly) {
            return adVideos;
        }
        return putAdsToPositionInFeed({
            adVideos,
            videos: feedVideos,
            positions,
        });
    }, [isAdsOnly, adVideos, feedVideos, positions]);
    useEffect(() => {
        if (!loading && !adVideosLoading) {
            actions.setVideos({ videos });
        }
    }, [loading, adVideosLoading, videos]);
    const processThumbnailImpressionQueue_throttled = useMemo(() => throttle(actions.processThumbnailImpressionQueue, 1000, {
        leading: false,
    }), []);
    useEffect(() => {
        if (publisherClient === null || publisherClient === void 0 ? void 0 : publisherClient.config) {
            actions.fetchedParams({
                fetchedParams: publisherClient.config,
            });
            actions.fetchExperiments({ publisherClient });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publisherClient]);
    useEffect(() => {
        if (channel === null || channel === void 0 ? void 0 : channel.config) {
            actions.fetchedParams({
                fetchedParams: channel.config,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel]);
    if (isAdsOnly && videos.length === 0) {
        return null;
    }
    return (<>
      <GlobalCustomCSS params={params}/>
      <Grid store={store} appContext={appContext} publisherClient={publisherClient} params={params} placeholders={placeholders} videos={!loading && !adVideosLoading ? videos : []} playlists={playlists} fetchMoreVideos={feedActions.loadMore} hasMore={hasMore} player={player} actions={Object.assign(Object.assign({}, actions), { processThumbnailImpressionQueue_throttled })} largeCarousel={largeCarousel}/>
      <ErrorBoundary discardOnError={true}>
        <QuantcastTag />
      </ErrorBoundary>
    </>);
};
const ConnectedEmbedFeed = connect(mapStateToProps)((props) => {
    const { params, actions } = props;
    const appContext = useEmbedAppContext({
        params,
        actions,
        appContextType: AppContextType.WIDGET_CONTEXT_TYPE,
    });
    if (!appContext) {
        return null;
    }
    return <PureEmbedFeed {...props} appContext={appContext}/>;
});
const EmbedFeed = ({ initialParams, widgetParams }) => (<WidgetWrapper initialParams={initialParams} widgetParams={widgetParams}>
    {(actions) => <ConnectedEmbedFeed actions={actions}/>}
  </WidgetWrapper>);
export default EmbedFeed;
